import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Sun, Thermometer, Plane, Radio, Map, Camera, Moon,
  Sunrise, Sunset, ChevronRight
} from "lucide-react";
import HourlyChart from "../HourlyChart";
import ExtendedForecast from "../ExtendedForecast";
import AirQualityIndex from "../AirQualityIndex";
import WeatherCalendar from "../WeatherCalendar";
import WeatherUniversity from "../WeatherUniversity";
import CustomAlerts from "../CustomAlerts";
import FlightWeather from "../FlightWeather";
import StormChasers from "../StormChasers";
import WeatherCommunity from "../WeatherCommunity";
import PersonalWeatherStation from "../PersonalWeatherStation";
import AdvancedVisualizations from "../AdvancedVisualizations";
import OfflineMode from "../OfflineMode";
import MarineForecast from "../MarineForecast";
import SkiReport from "../SkiReport";
import GardeningForecast from "../GardeningForecast";
import HistoricalWeather from "../HistoricalWeather";
import RadarMap from "../RadarMap";
import PrecipPinTracker from "../PrecipPinTracker";
import { FishingWidget } from "./FishingWidget";
import { LakeRiverSportWidget } from "./LakeRiverSportWidget";
import { SportsFieldWeatherCard } from "./SportsFieldWeatherCard";
import { EventWeatherCard } from "./EventWeatherCard";
import {
  PremiumLock,
  TryPremiumButton,
} from "../PremiumFeatures";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } }
};
const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export function ReorderableWidgets({
  dashboardWidgets, isWidgetVisible, forecast, extendedForecast, weather,
  tempUnit, isPremium, location, token, isDarkMode, savedLocations, moonData,
  formatTime, HuntingWidget,
  setLocation, setWeather, setForecast, setExtendedForecast,
}) {
  const navigate = useNavigate();
  const [radarLoaded, setRadarLoaded] = useState(false);
  const halfWidth = { order: 0, width: 'calc(50% - 0.75rem)', minWidth: '300px', flex: '1 1 auto' };
  const fullWidth = { order: 0, width: '100%' };

  return (
    <motion.div
      key={dashboardWidgets ? JSON.stringify(dashboardWidgets.map(w => w.order)) : 'default'}
      variants={container}
      initial="hidden"
      animate="show"
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-6"
      style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}
    >
      {/* 1. 24-Hour Forecast */}
      {isWidgetVisible('hourly') && (
        <motion.div variants={item} style={{ ...fullWidth, order: 1 }} className="glass rounded-3xl p-5" data-testid="hourly-forecast-card">
          <h3 className="font-semibold mb-4 text-white/80">24-Hour Forecast</h3>
          {forecast?.hourly && <HourlyChart data={forecast.hourly.slice(0, 24)} tempUnit={tempUnit} />}
        </motion.div>
      )}

      {/* 2. 15-Day Extended Forecast */}
      {isWidgetVisible('extended') && (
        <motion.div variants={item} style={{ ...fullWidth, order: 2 }} className="glass rounded-3xl p-5" data-testid="extended-forecast-card">
          <PremiumLock feature="15-Day Forecast" canAccess={isPremium}>
            {extendedForecast?.daily && <ExtendedForecast data={extendedForecast.daily} tempUnit={tempUnit} />}
          </PremiumLock>
        </motion.div>
      )}

      {/* 3. Rain/Snow Tracker */}
      {isWidgetVisible('precip-pins') && (
        <motion.div variants={item} style={{ ...fullWidth, order: 3 }} data-testid="precip-pin-tracker-card">
          <PremiumLock feature="Rain/Snow Tracker" canAccess={isPremium}>
            <PrecipPinTracker location={location} savedLocations={savedLocations} isDarkMode={isDarkMode} />
          </PremiumLock>
        </motion.div>
      )}

      {/* 4. Sun, Moon & Photography */}
      {weather && moonData && (
        <motion.div variants={item} style={{ ...fullWidth, order: 4 }} className="glass rounded-3xl p-5" data-testid="sun-moon-photography-card">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-semibold mb-3 text-white/80 flex items-center gap-2">
                <Sun className="w-4 h-4 text-[#ccff00]" /> Sun
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Sunrise className="w-4 h-4 text-[#ccff00]" /><span className="text-white/60 text-sm">Sunrise</span></div>
                  <span className="font-mono font-bold text-sm">{formatTime(weather.sys.sunrise)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Sunset className="w-4 h-4 text-[#ff0080]" /><span className="text-white/60 text-sm">Sunset</span></div>
                  <span className="font-mono font-bold text-sm">{formatTime(weather.sys.sunset)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/40 text-sm">Daylight</span>
                  <span className="font-mono text-sm text-white/60">
                    {(() => { const diff = weather.sys.sunset - weather.sys.sunrise; return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`; })()}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-3 text-white/80 flex items-center gap-2">
                <Moon className="w-4 h-4 text-[#7000ff]" /> Moon
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><span className="text-lg">{moonData.phase.icon}</span><span className="text-white/60 text-sm">{moonData.phase.name}</span></div>
                  <span className="text-xs text-white/40">{moonData.phase.illumination}%</span>
                </div>
                <div className="flex items-center justify-between"><span className="text-white/60 text-sm">Moonrise</span><span className="font-mono font-bold text-sm">{moonData.moonrise}</span></div>
                <div className="flex items-center justify-between"><span className="text-white/60 text-sm">Moonset</span><span className="font-mono font-bold text-sm">{moonData.moonset}</span></div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-3 text-white/80 flex items-center gap-2">
                <Camera className="w-4 h-4 text-[#ff9500]" /> Photography
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-white/40 mb-1">Morning Golden Hour</p>
                  <span className="font-mono text-sm">
                    {(() => { const s = new Date(weather.sys.sunrise * 1000); const a = new Date(s.getTime() - 30 * 60000); const b = new Date(s.getTime() + 60 * 60000); return `${a.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'})} - ${b.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'})}`; })()}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-white/40 mb-1">Evening Golden Hour</p>
                  <span className="font-mono text-sm">
                    {(() => { const s = new Date(weather.sys.sunset * 1000); const a = new Date(s.getTime() - 60 * 60000); const b = new Date(s.getTime() + 30 * 60000); return `${a.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'})} - ${b.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'})}`; })()}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-white/40 mb-1">Blue Hour</p>
                  <span className="font-mono text-sm">
                    {(() => { const s = new Date(weather.sys.sunset * 1000); const a = new Date(s.getTime() + 20 * 60000); const b = new Date(s.getTime() + 50 * 60000); return `${a.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'})} - ${b.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'})}`; })()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* 5. Air Quality Index */}
      {isWidgetVisible('minutely') && (
        <motion.div variants={item} style={{ ...halfWidth, order: 5 }} className="glass rounded-3xl p-5" data-testid="aqi-card">
          <AirQualityIndex lat={location?.lat} lon={location?.lon} />
        </motion.div>
      )}

      {/* 6. UV Index */}
      {weather && (
        <motion.div variants={item} style={{ ...halfWidth, order: 6 }} className="glass rounded-3xl p-5" data-testid="uv-index-widget">
          <h3 className="font-semibold mb-3 text-white/80 flex items-center gap-2"><Sun className="w-4 h-4 text-[#ff9500]" /> UV Index</h3>
          <div className="space-y-3">
            {(() => {
              const hour = new Date().getHours(); const clouds = weather.clouds?.all || 0;
              let uvi = 0;
              if (hour >= 6 && hour <= 18) { if (hour >= 10 && hour <= 14) uvi = 8; else if (hour >= 8 && hour <= 16) uvi = 5; else uvi = 2; uvi = Math.max(1, Math.round(uvi * (1 - clouds / 150))); }
              let level, color, advice;
              if (uvi <= 2) { level = "Low"; color = "#4ade80"; advice = "No protection needed"; }
              else if (uvi <= 5) { level = "Moderate"; color = "#fbbf24"; advice = "Wear sunscreen"; }
              else if (uvi <= 7) { level = "High"; color = "#f97316"; advice = "Reduce sun exposure"; }
              else if (uvi <= 10) { level = "Very High"; color = "#ef4444"; advice = "Extra protection needed"; }
              else { level = "Extreme"; color = "#a855f7"; advice = "Avoid sun exposure"; }
              return (<><div className="flex items-center justify-between"><div className="flex items-center gap-3"><div className="text-3xl font-bold" style={{ color }}>{uvi}</div><div><p className="font-semibold" style={{ color }}>{level}</p><p className="text-xs text-white/40">of 11</p></div></div></div><p className="text-sm text-white/60">{advice}</p></>);
            })()}
          </div>
        </motion.div>
      )}

      {/* 7. Custom Alerts */}
      {isWidgetVisible('alerts') && (
        <motion.div variants={item} style={{ ...halfWidth, order: 7 }} className="glass rounded-3xl p-5" data-testid="custom-alerts-card">
          <PremiumLock feature="Advanced Custom Alerts" canAccess={isPremium}>
            <CustomAlerts token={token} isDarkMode={isDarkMode} savedLocations={savedLocations} />
          </PremiumLock>
        </motion.div>
      )}

      {/* 8. Comfort Level */}
      {weather && (
        <motion.div variants={item} style={{ ...halfWidth, order: 8 }} className="glass rounded-3xl p-5" data-testid="comfort-level-widget">
          <h3 className="font-semibold mb-3 text-white/80 flex items-center gap-2"><Thermometer className="w-4 h-4 text-[#00f2ea]" /> Comfort Level</h3>
          <div className="space-y-3">
            {(() => {
              const temp = weather.main.temp; const humidity = weather.main.humidity; const windSpeed = weather.wind?.speed || 0;
              let score = 100;
              if (temp < 32) score -= 40; else if (temp < 50) score -= 20; else if (temp > 90) score -= 35; else if (temp > 80) score -= 15;
              if (humidity > 80) score -= 20; else if (humidity > 60) score -= 10; else if (humidity < 20) score -= 10;
              if (windSpeed > 20) score -= 15; else if (windSpeed > 10) score -= 5;
              score = Math.max(0, Math.min(100, score));
              let label, color;
              if (score >= 80) { label = "Excellent"; color = "#4ade80"; } else if (score >= 60) { label = "Good"; color = "#a3e635"; }
              else if (score >= 40) { label = "Fair"; color = "#fbbf24"; } else if (score >= 20) { label = "Poor"; color = "#f97316"; }
              else { label = "Harsh"; color = "#ef4444"; }
              return (<><div className="flex items-center justify-between"><div className="flex items-center gap-3"><div className="text-3xl font-bold" style={{ color }}>{score}</div><div><p className="font-semibold" style={{ color }}>{label}</p><p className="text-xs text-white/40">of 100</p></div></div></div><div className="flex justify-between text-sm text-white/50"><span>Humidity: {humidity}%</span><span>Wind: {Math.round(windSpeed)} mph</span></div></>);
            })()}
          </div>
        </motion.div>
      )}

      {/* 9. Calendar + Community/University Stack */}
      <motion.div variants={item} style={{ ...fullWidth, order: 9 }} className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 items-start" data-testid="calendar-learning-community-grid">
        <div className="glass rounded-3xl p-5 h-[590px] overflow-y-auto" data-testid="weather-calendar-card">
          <PremiumLock feature="Weather Calendar" canAccess={isPremium}>
            <WeatherCalendar token={token} location={location} forecast={extendedForecast} isDarkMode={isDarkMode} />
          </PremiumLock>
        </div>

        <div className="grid grid-rows-[250px_316px] gap-4 md:gap-6 h-[590px] min-h-0" data-testid="calendar-side-stack">
          <div className="glass rounded-3xl p-4 md:p-5 min-h-0 overflow-y-auto" data-testid="weather-community-card">
            <PremiumLock feature="Weather Community" canAccess={isPremium}>
              <WeatherCommunity token={token} location={location} user={null} isDarkMode={isDarkMode} compact />
            </PremiumLock>
          </div>

          <div className="glass rounded-3xl p-4 md:p-5 min-h-0 overflow-y-auto" data-testid="weather-university-card">
            <PremiumLock feature="Weather University" canAccess={isPremium}>
              <WeatherUniversity isDarkMode={isDarkMode} />
            </PremiumLock>
          </div>
        </div>
      </motion.div>

      {/* 10. Sport Weather Cards */}
      <motion.div variants={item} style={{ ...fullWidth, order: 10 }} className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-start" data-testid="sport-weather-card-grid">
        {isWidgetVisible('marine') && (
          <div className="glass rounded-3xl p-5 cursor-pointer hover:ring-2 hover:ring-blue-400/30 transition-all h-[590px] overflow-y-auto" data-testid="marine-forecast-card"
            onClick={() => isPremium && location && navigate(`/marine?lat=${location.lat}&lon=${location.lon}`)}>
            <PremiumLock feature="Lake Forecast" canAccess={isPremium}>
              {location && <MarineForecast lat={location.lat} lon={location.lon} isDarkMode={isDarkMode} compact />}
              {isPremium && (
                <div className="flex items-center justify-center gap-4 mt-3">
                  <div className={`flex items-center gap-1 text-xs font-medium ${isDarkMode ? 'text-blue-400/70' : 'text-blue-600'}`}>Click for detailed lake forecast<ChevronRight className="w-3 h-3" /></div>
                  <button data-testid="marine-card-lake-monitor-button" onClick={(e) => { e.stopPropagation(); navigate(`/lake?lat=${location.lat}&lon=${location.lon}`); }}
                    className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg ${isDarkMode ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'} transition-colors`}>
                    Lake Monitor<ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              )}
            </PremiumLock>
          </div>
        )}

        {isWidgetVisible('lake-river') && (
          <div className="glass rounded-3xl p-5 cursor-pointer hover:ring-2 hover:ring-blue-400/30 transition-all h-[590px] overflow-y-auto" data-testid="lake-river-sport-card"
            onClick={() => isPremium && location && navigate(`/lake?lat=${location.lat}&lon=${location.lon}`)}>
            <PremiumLock feature="River Conditions" canAccess={isPremium}>
              {location && <LakeRiverSportWidget lat={location.lat} lon={location.lon} isDarkMode={isDarkMode} />}
              {isPremium && (<div className={`flex items-center justify-center gap-1 mt-3 text-xs font-medium ${isDarkMode ? 'text-blue-400/70' : 'text-blue-600'}`}>Open full river monitor<ChevronRight className="w-3 h-3" /></div>)}
            </PremiumLock>
          </div>
        )}

        {isWidgetVisible('fishing') && (
          <div className="glass rounded-3xl p-5 cursor-pointer hover:ring-2 hover:ring-emerald-400/30 transition-all h-[590px] overflow-y-auto" data-testid="fishing-conditions-card"
            onClick={() => isPremium && location && navigate(`/fishing?lat=${location.lat}&lon=${location.lon}`)}>
            <PremiumLock feature="Fishing Conditions" canAccess={isPremium}>
              {location && <FishingWidget lat={location.lat} lon={location.lon} isDarkMode={isDarkMode} />}
              {isPremium && (<div className={`flex items-center justify-center gap-1 mt-3 text-xs font-medium ${isDarkMode ? 'text-emerald-400/70' : 'text-emerald-700'}`}>Open full fishing conditions<ChevronRight className="w-3 h-3" /></div>)}
            </PremiumLock>
          </div>
        )}

        {isWidgetVisible('hunting') && (
          <div className="glass rounded-3xl p-5 cursor-pointer hover:ring-2 hover:ring-amber-400/30 transition-all h-[590px] overflow-y-auto" data-testid="hunting-scouting-card"
            onClick={() => isPremium && location && navigate(`/hunting?lat=${location.lat}&lon=${location.lon}`)}>
            <PremiumLock feature="Hunting & Scouting" canAccess={isPremium}>
              <HuntingWidget lat={location?.lat} lon={location?.lon} isDarkMode={isDarkMode} />
              {isPremium && (<div className={`flex items-center justify-center gap-1 mt-3 text-xs font-medium ${isDarkMode ? 'text-amber-400/70' : 'text-amber-600'}`}>Open full hunting conditions<ChevronRight className="w-3 h-3" /></div>)}
            </PremiumLock>
          </div>
        )}

        {isWidgetVisible('ski') && (
          <div className="glass rounded-3xl p-5 cursor-pointer hover:ring-2 hover:ring-cyan-400/30 transition-all h-[590px] overflow-y-auto" data-testid="ski-report-card"
            onClick={() => isPremium && location && navigate(`/ski?lat=${location.lat}&lon=${location.lon}`)}>
            <PremiumLock feature="Snow & Ski Reports" canAccess={isPremium}>
              {location && <SkiReport lat={location.lat} lon={location.lon} isDarkMode={isDarkMode} compact />}
              {isPremium && (<div className={`flex items-center justify-center gap-1 mt-3 text-xs font-medium ${isDarkMode ? 'text-cyan-400/70' : 'text-cyan-600'}`}>Click for detailed ski report<ChevronRight className="w-3 h-3" /></div>)}
            </PremiumLock>
          </div>
        )}

        {isWidgetVisible('flight') && (
          <div className="glass rounded-3xl p-5 h-[590px] overflow-hidden" data-testid="flight-weather-card">
            <PremiumLock feature="Air Travel Weather" canAccess={isPremium}>
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Plane className="w-5 h-5 text-[#00f2ea] shrink-0" />
                  <div className="min-w-0">
                    <h3 className="font-semibold text-white/80">Air Travel Weather</h3>
                    <p className="text-xs text-white/45 truncate">Flight lookup and route conditions</p>
                  </div>
                </div>
              </div>
              <div className="h-[510px]">
                <FlightWeather splitLayout />
              </div>
            </PremiumLock>
          </div>
        )}
      </motion.div>

      {/* 11. Sports Field Weather */}
      <motion.div variants={item} style={{ ...fullWidth, order: 11 }} data-testid="sports-field-weather-section">
        <PremiumLock feature="Sports Field Weather" canAccess={isPremium}>
          <SportsFieldWeatherCard lat={location?.lat} lon={location?.lon} isDarkMode={isDarkMode} />
        </PremiumLock>
      </motion.div>

      {/* 12. Festival & Event Weather */}
      <motion.div variants={item} style={{ ...fullWidth, order: 12 }} data-testid="event-weather-section">
        <PremiumLock feature="Festival & Event Weather" canAccess={isPremium}>
          <EventWeatherCard lat={location?.lat} lon={location?.lon} isDarkMode={isDarkMode} />
        </PremiumLock>
      </motion.div>

      {/* 13. Gardening */}
      {isWidgetVisible('gardening') && (
        <motion.div variants={item} style={{ ...halfWidth, order: 13 }} className="glass rounded-3xl p-5" data-testid="gardening-forecast-card">
          <PremiumLock feature="Gardening & Agriculture" canAccess={isPremium}>
            {location && <GardeningForecast lat={location.lat} lon={location.lon} isDarkMode={isDarkMode} />}
          </PremiumLock>
        </motion.div>
      )}

      {/* 14. Historical */}
      {isWidgetVisible('historical') && (
        <motion.div variants={item} style={{ ...halfWidth, order: 14 }} className="glass rounded-3xl p-5" data-testid="historical-weather-card">
          <PremiumLock feature="Historical Weather & Almanac" canAccess={isPremium}>
            {location && <HistoricalWeather lat={location.lat} lon={location.lon} isDarkMode={isDarkMode} />}
          </PremiumLock>
        </motion.div>
      )}

      {/* 15. Storm Chasers */}
      {isWidgetVisible('storm') && (
        <motion.div variants={item} style={{ ...halfWidth, order: 15 }} className="glass rounded-3xl p-5" data-testid="storm-chasers-card">
          <PremiumLock feature="Storm Chasers & Streams" canAccess={isPremium}>
            <div className="flex items-center gap-2 mb-4"><Radio className="w-5 h-5 text-[#ff0080]" /><h3 className="font-semibold text-white/80">Storm Chasers & Streams</h3></div>
            {location && <StormChasers lat={location.lat} lon={location.lon} />}
          </PremiumLock>
        </motion.div>
      )}

      {/* 16. Personal Weather Stations */}
      {isWidgetVisible('pws') && (
        <motion.div variants={item} style={{ ...halfWidth, order: 16 }} className="glass rounded-3xl p-5" data-testid="pws-card">
          <PremiumLock feature="Personal Weather Stations" canAccess={isPremium}>
            <PersonalWeatherStation token={token} lat={location?.lat} lon={location?.lon} isDarkMode={isDarkMode} />
          </PremiumLock>
        </motion.div>
      )}

      {/* 17. Advanced Viz */}
      {isWidgetVisible('visualizations') && (
        <motion.div variants={item} style={{ ...halfWidth, order: 17 }} className="glass rounded-3xl p-5" data-testid="advanced-viz-card">
          <PremiumLock feature="Advanced Visualizations" canAccess={isPremium}>
            <AdvancedVisualizations token={token} lat={location?.lat} lon={location?.lon} isDarkMode={isDarkMode} />
          </PremiumLock>
        </motion.div>
      )}

      {/* 18. Offline Mode */}
      <motion.div variants={item} style={{ ...halfWidth, order: 18 }} className="glass rounded-3xl p-5" data-testid="offline-mode-card">
        <PremiumLock feature="Offline Mode" canAccess={isPremium}>
          <OfflineMode location={location} forecast={forecast} weather={weather} currentWeather={weather} extendedForecast={extendedForecast} isDarkMode={isDarkMode}
            onLoadCached={(cachedData) => {
              if (cachedData.location) setLocation(cachedData.location);
              if (cachedData.weather) setWeather(cachedData.weather);
              if (cachedData.forecast) setForecast(cachedData.forecast);
              if (cachedData.extendedForecast) setExtendedForecast(cachedData.extendedForecast);
            }} />
        </PremiumLock>
      </motion.div>

      {/* 19. Radar Map */}
      {isWidgetVisible('radar') && (
        <motion.div variants={item} style={{ order: 19, minHeight: '700px', width: '100%' }} className="glass rounded-3xl overflow-hidden relative" data-testid="radar-map-card">
          {(!location || !radarLoaded) && (
            <div className="absolute top-4 left-4 z-20 flex items-center gap-2" data-testid="radar-card-placeholder-header">
              <div className="p-2 rounded-xl bg-black/50 backdrop-blur-md"><Map className="w-5 h-5 text-[#00f2ea]" /></div>
              <h3 className="font-semibold">Advanced Weather Radar</h3>
              {!isPremium && <TryPremiumButton feature="3-Day Radar" className="ml-2" />}
            </div>
          )}
          {location && radarLoaded ? (
            <RadarMap lat={location.lat} lon={location.lon} locationName={location.name} isDarkMode={isDarkMode} />
          ) : (
            <div className="min-h-[700px] flex flex-col items-center justify-center p-6" data-testid="radar-on-demand-panel">
              <button
                onClick={() => setRadarLoaded(true)}
                className="px-5 py-3 rounded-xl bg-[#00f2ea] text-black font-bold hover:bg-[#00d4d4] transition-colors"
                data-testid="radar-load-button"
              >
                Open Radar Map
              </button>
              <p className="mt-3 text-xs text-white/45 text-center">Live RainViewer radar can be enabled inside the map.</p>
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
}